import { useParams, useNavigate } from 'react-router-dom'
import {
  Stack, Title, Group, Button, Tabs, Text, Paper,
  SimpleGrid, Box, Badge, Divider, Alert, Skeleton, Textarea,
  Modal, MultiSelect, TextInput,
} from '@mantine/core'
import { IconArrowLeft, IconAlertCircle, IconUsers, IconHistory, IconRocket } from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { api } from '@/api/client'
import { ccmApi, APPROVER_ROLE_LABELS, type TenantCommittee } from '@/api/ccm'
import { useAuthStore, useCurrentTenant, useCan } from '@/store/auth'
import {
  GmudClassificationBadge, GmudRiskBadge, StatusBadge,
  ProtocolNumberBadge, GmudCabPanel,
} from '@/components/chamados'
import {
  JanelaExecucaoFields, janelaValida, type JanelaValor,
} from '@/components/gmud/JanelaExecucaoFields'
import { instantToWallClock, wallClockToInstant } from '@/components/gmud/tzWindow'
import { selectableUsers, type MixedUserRow } from '@/utils/realUsers'
import { useState } from 'react'

interface GmudDetail {
  id: number
  title: string
  description: string
  status: string
  /** A GMUD pode ser cancelada (encerrada antes de executar) a partir do
   *  status atual? Vem do backend de propósito: quais estados aceitam depende
   *  da classificação, e um mapa espelhado aqui divergiria calado da máquina
   *  de estados — oferecendo botão que `close_gmud` recusa. */
  can_cancel?: boolean
  protocol_number: string | null
  gmud_classification: string
  risk_level: string
  rollback_plan: string | null
  rollback_time_minutes: number | null
  rollback_trigger_criteria: string | null
  execution_status: string | null
  execution_notes: string | null
  pir_notes: string | null
  cab_quorum_required: number
  cab_meeting_date: string | null
  affected_systems: string[] | null
  communication_plan: string | null
  scheduled_start: string | null
  scheduled_end: string | null
  /** IANA tz do tenant. null = nao cadastrado — a UI pergunta, nao adivinha. */
  tenant_timezone?: string | null
  current_environment: string | null
  origin_tenant_id: number | null
  requested_by_user_id: number
  requested_by_name: string
  created_at: string
  approvals: Array<{
    approver_user_id: number
    approver_name: string
    approver_role: string
    decision: string | null
    decided_at: string | null
  }>
  events: Array<{
    id: number
    actor_name: string
    event_type: string
    from_status: string | null
    to_status: string | null
    note: string | null
    created_at: string
  }>
}

function fmt(dt: string | null): string {
  if (!dt) return '—'
  return new Date(dt).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })
}

/** 403 do backend (axios expoe o status em `err.response.status`, ver api/client.ts). */
function isForbidden(err: unknown): boolean {
  return (err as { response?: { status?: number } })?.response?.status === 403
}

function apiErrorMessage(err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof detail === 'string' ? detail : 'Operação falhou. Tente novamente.'
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="sm">{value ?? '—'}</Text>
    </Box>
  )
}

export function GmudDetalhe() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const tenantId = useCurrentTenant()?.id
  const { user } = useAuthStore()
  const can = useCan()
  // Acoes de FLUXO (submeter, encaminhar ao CCM, agendar, executar...) saem de
  // permissao — quem nao gerencia GMUD nao pode oferecer a acao ao backend,
  // que ja recusa (gmud_manage).
  const podeGerenciar = can('chamados', 'gmud_manage')
  const queryClient = useQueryClient()
  const [execNotes, setExecNotes] = useState('')
  const [pirNotes, setPirNotes] = useState('')
  const [cabModalOpen, setCabModalOpen] = useState(false)
  const [cabApprovers, setCabApprovers] = useState<string[]>([])
  const [cabMeetingDate, setCabMeetingDate] = useState('')
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [schedModalOpen, setSchedModalOpen] = useState(false)
  // Agendar usa os MESMOS campos do submeter: horario de parede no fuso do tenant,
  // convertido em instante antes de enviar. Era um TextInput de ISO cru — texto sem
  // offset virava UTC no banco, e desde que a janela passou a decidir quando WAF/ZTNA
  // sao aplicados (consoleai#26157) isso mudaria a hora real da mudanca.
  const [schedJanela, setSchedJanela] = useState<JanelaValor>({ start: null, end: null })
  const [schedTzEscolhido, setSchedTzEscolhido] = useState<string | null>(null)
  const [submitModalOpen, setSubmitModalOpen] = useState(false)
  const [submitJanela, setSubmitJanela] = useState<JanelaValor>({ start: null, end: null })
  const [submitTzEscolhido, setSubmitTzEscolhido] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery<GmudDetail>({
    queryKey: ['gmud', tenantId, id],
    queryFn: async () => (await api.get<GmudDetail>(`/v1/tenants/${tenantId}/gmud/${id}`)).data,
    enabled: !!tenantId && !!id,
    retry: false,
  })

  const onApiError = (err: unknown) =>
    notifications.show({ color: 'red', message: apiErrorMessage(err) })

  const submitMutation = useMutation({
    mutationFn: async (window?: { scheduled_start: string; scheduled_end: string }) =>
      api.post(`/v1/tenants/${tenantId}/gmud/${id}/submit`, window ?? {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gmud', tenantId, id] })
      setSubmitModalOpen(false)
      notifications.show({ color: 'green', message: 'GMUD submetida.' })
    },
    onError: onApiError,
  })

  const startReviewMutation = useMutation({
    mutationFn: async () => api.post(`/v1/tenants/${tenantId}/gmud/${id}/start-review`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gmud', tenantId, id] })
      notifications.show({ color: 'green', message: 'Revisão técnica iniciada.' })
    },
    onError: onApiError,
  })

  const { data: usersData } = useQuery<{ items: Array<MixedUserRow & { full_name: string; email: string }> }>({
    queryKey: ['users-list-for-cab'],
    queryFn: async () => (await api.get('/v1/users')).data,
    // Só é a fonte quando NÃO há comitê. Com comitê a lista sai do cadastro.
    enabled: cabModalOpen,
    staleTime: 120_000,
  })

  // Fatia 3: o backend recusa aprovador fora do comitê. Mostrar /v1/users aqui
  // ofereceria gente que o send-to-cab vai rejeitar — erro depois de preencher.
  const { data: comite, isLoading: comiteLoading } = useQuery<TenantCommittee | null>({
    queryKey: ['gmud-committee', tenantId],
    queryFn: async () => (await ccmApi.forTenant(Number(tenantId))).data,
    enabled: cabModalOpen && !!tenantId,
    staleTime: 120_000,
  })

  const sendToCabMutation = useMutation({
    mutationFn: async () =>
      api.post(`/v1/tenants/${tenantId}/gmud/${id}/send-to-cab`, {
        approver_ids: cabApprovers.map(Number),
        cab_meeting_date: cabMeetingDate || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gmud', tenantId, id] })
      setCabModalOpen(false)
      notifications.show({ color: 'green', message: 'GMUD encaminhada ao CCM. Membros notificados.' })
    },
    onError: onApiError,
  })

  const scheduleMutation = useMutation({
    mutationFn: async (janela: { scheduled_start: string; scheduled_end: string }) =>
      api.post(`/v1/tenants/${tenantId}/gmud/${id}/schedule`, janela),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gmud', tenantId, id] })
      setSchedModalOpen(false)
      notifications.show({ color: 'green', message: 'Janela de execução agendada.' })
    },
    onError: onApiError,
  })

  const startExecMutation = useMutation({
    mutationFn: async () => api.post(`/v1/tenants/${tenantId}/gmud/${id}/start-execution`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gmud', tenantId, id] })
      notifications.show({ color: 'green', message: 'Execução iniciada.' })
    },
    onError: onApiError,
  })

  const closeMutation = useMutation({
    mutationFn: async () => api.post(`/v1/tenants/${tenantId}/gmud/${id}/close`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gmud', tenantId, id] })
      notifications.show({ color: 'green', message: 'GMUD fechada.' })
    },
    onError: onApiError,
  })

  // Mesma rota do fechamento, ato diferente: aqui a mudança NÃO executou, e o
  // backend exige `note` (`cancelamento_exige_motivo`). Fica separado do
  // `closeMutation` porque o que se diz ao operador no fim também é outro — e
  // porque um único botão para os dois casos obrigaria motivo onde ele não é
  // devido, no fechamento de execução já registrada.
  const cancelMutation = useMutation({
    mutationFn: async (note: string) =>
      api.post(`/v1/tenants/${tenantId}/gmud/${id}/close`, { note }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gmud', tenantId, id] })
      setCancelModalOpen(false)
      setCancelReason('')
      notifications.show({
        color: 'green',
        message: 'GMUD cancelada. O que dependia dela foi liberado.',
      })
    },
    onError: onApiError,
  })

  const finishExecMutation = useMutation({
    mutationFn: async (executionStatus: string) =>
      api.post(`/v1/tenants/${tenantId}/gmud/${id}/finish-execution`, {
        execution_status: executionStatus,
        execution_notes: execNotes,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gmud', tenantId, id] })
      notifications.show({ color: 'green', message: 'Resultado registrado.' })
    },
    onError: onApiError,
  })

  const ratifyMutation = useMutation({
    mutationFn: async () =>
      api.post(`/v1/tenants/${tenantId}/gmud/${id}/ratify`, { pir_notes: pirNotes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gmud', tenantId, id] })
      notifications.show({ color: 'green', message: 'GMUD ratificada e fechada.' })
    },
    onError: onApiError,
  })

  if (isLoading) return <Skeleton height={300} role="status" aria-label="Carregando detalhes da GMUD" />
  // A rota nao e mais gateada por permissao (ver App.tsx): a decisao de leitura
  // e do backend, entao o 403 `forbidden:gmud_read` chega ATE AQUI. Recusa de
  // acesso nao pode ser anunciada como falha de rede — "verifique sua conexao"
  // manda o usuario caçar problema onde nao ha.
  if (isForbidden(error)) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={16} />}>
        Você não tem acesso a esta GMUD.
      </Alert>
    )
  }
  if (error || !data) return <Alert color="red" icon={<IconAlertCircle size={16} />}>Não foi possível carregar a GMUD. Verifique sua conexão e tente novamente.</Alert>

  // Emergencial sai pela ratificação, tanto quando deu certo (`gmud_ratification`)
  // quanto quando falhou (`gmud_rolled_back`): é ali que outra pessoa abona.
  const emergenciaARatificar = data.gmud_classification === 'emergency'
    && ['gmud_ratification', 'gmud_rolled_back'].includes(data.status)
  const currentUserId = user?.id ? Number(user.id) : 0
  // O botao de voto NAO sai de permissao: sai de RELACIONAMENTO — meu
  // user_id esta em `approvals` com `decision` nulo. Permissao nao serve
  // aqui porque nao sabe de QUAL GMUD se trata, e o `decision` importa
  // porque o voto e unico (sem ele o botao continuaria oferecido a quem
  // ja votou).
  // O solicitante NUNCA vota, mesmo nomeado: `submit_cab_vote` recusa com
  // `solicitante_nao_pode_votar` (segregação PCI). Existe passivo de GMUDs
  // gravadas antes da correção do send-to-cab com o solicitante em
  // `gmud_approvals` — nelas, sem esta exclusão a tela ofereceria o voto e a
  // API devolveria 403.
  const souAprovadorPendente =
    currentUserId !== data.requested_by_user_id &&
    data.approvals.some(
      (a) => a.approver_user_id === currentUserId && a.decision == null,
    )

  return (
    <Stack gap="md">
      <Group>
        {/* `navigate(-1)`, nao `/gmud`: a lista e gateada por
            `chamados:gmud_read` e o detalhe nao (a autoridade e o backend, por
            cinco fontes, tres delas de relacionamento). O aprovador nomeado sem
            a permissao entra aqui e tomava Forbidden ao voltar. Voltar ao
            historico tambem serve quem chegou por link de e-mail. */}
        <Button variant="subtle" leftSection={<IconArrowLeft size={14} />} onClick={() => navigate(-1)} aria-label="Voltar">
          Voltar
        </Button>
        <ProtocolNumberBadge value={data.protocol_number} withCopy />
        <Title order={4} style={{ flex: 1 }}>{data.title}</Title>
        <GmudClassificationBadge classification={data.gmud_classification} />
        <GmudRiskBadge risk={data.risk_level} />
        <StatusBadge status={data.status} withIcon />
        {data.current_environment && (
          <Badge variant="light" color="grape">Ambiente: {data.current_environment}</Badge>
        )}
        {data.origin_tenant_id && (
          <Badge variant="light" color="gray">Origem: esteira de promoção</Badge>
        )}
      </Group>

      {/* Acoes principais — todas de FLUXO, gateadas por gmud_manage */}
      <Group>
        {podeGerenciar && data.status === 'gmud_draft' && (
          <Button size="xs"
            onClick={() => data.scheduled_start ? submitMutation.mutate(undefined) : setSubmitModalOpen(true)}
            loading={submitMutation.isPending}>
            Submeter GMUD
          </Button>
        )}
        {podeGerenciar && data.status === 'gmud_submitted' && (
          <Button size="xs" onClick={() => startReviewMutation.mutate()} loading={startReviewMutation.isPending}>
            Iniciar Revisão Técnica
          </Button>
        )}
        {podeGerenciar && data.status === 'gmud_technical_review' && (
          currentUserId === data.requested_by_user_id ? (
            <Group gap="xs">
              <Button size="xs" disabled>Encaminhar ao CCM</Button>
              <Text size="xs" c="dimmed">
                Segregação PCI: o solicitante não pode encaminhar ao CCM — peça a outro gestor.
              </Text>
            </Group>
          ) : (
            <Button size="xs" onClick={() => setCabModalOpen(true)}>
              Encaminhar ao CCM
            </Button>
          )
        )}
        {podeGerenciar && data.status === 'gmud_approved' && (
          <Button
            size="xs"
            onClick={() => {
              const tz = data.tenant_timezone
              setSchedJanela({
                start: tz && data.scheduled_start ? instantToWallClock(new Date(data.scheduled_start), tz) : null,
                end: tz && data.scheduled_end ? instantToWallClock(new Date(data.scheduled_end), tz) : null,
              })
              setSchedModalOpen(true)
            }}
          >
            Agendar Execução
          </Button>
        )}
        {podeGerenciar && data.status === 'gmud_scheduled' && (
          <Button size="xs" onClick={() => startExecMutation.mutate()} loading={startExecMutation.isPending}>
            Iniciar Execução
          </Button>
        )}
        {/* Desistir da mudança. Vem do backend (`can_cancel`) e não de uma
            lista de status repetida aqui — ver o comentário no tipo.
            `variant="subtle"` e vermelho separam do botão de avançar sem
            precisar de `ml="auto"`: em `gmud_pending_cab` não há botão de
            fluxo nenhum, e alinhar à direita deixaria a única ação da tela
            sozinha na margem oposta à que o olho procura — justo no estado em
            que o pedido de WAF fica esperando. */}
        {podeGerenciar && data.can_cancel && (
          <Button size="xs" variant="subtle" color="red"
                  onClick={() => setCancelModalOpen(true)}>
            Cancelar GMUD
          </Button>
        )}
      </Group>

      <Modal opened={cancelModalOpen} onClose={() => setCancelModalOpen(false)}
             title="Cancelar GMUD">
        <Stack gap="sm">
          <Text size="sm">
            A mudança é encerrada sem executar, e o que estava preso a ela —
            provisionamento de WAF, adoção de ZTNA — é liberado. Não há como
            reabrir: para retomar, é preciso pedir de novo.
          </Text>
          {/* Obrigatório porque `close_gmud` recusa sem ele, e recusa por um
              motivo: encerrar mudança que não chegou ao fim sem dizer por quê é
              exatamente o que a auditoria pergunta depois. O texto vai para o
              histórico da GMUD como evento `closed`. */}
          <Textarea
            label="Motivo do cancelamento"
            description="Fica no histórico da GMUD."
            placeholder="Ex.: ALB errado — o pedido foi refeito no ALB de produção."
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
          <Group justify="flex-end">
            <Button size="xs" variant="default" onClick={() => setCancelModalOpen(false)}>
              Voltar
            </Button>
            <Button size="xs" color="red"
                    disabled={!cancelReason.trim()}
                    loading={cancelMutation.isPending}
                    onClick={() => cancelMutation.mutate(cancelReason.trim())}>
              Confirmar cancelamento
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={cabModalOpen} onClose={() => setCabModalOpen(false)} title="Encaminhar ao CCM" size="lg">
        <Stack gap="sm">
          {!comiteLoading && !comite && (
            <Alert color="yellow" icon={<IconAlertCircle size={16} />} variant="light">
              A organização deste tenant não tem <strong>comitê de mudança</strong>
              {' '}cadastrado. Os aprovadores abaixo <strong>não serão validados</strong>,
              e a GMUD seguirá sem validação de aprovador — a mesma coisa que o
              backend registra na auditoria. Cadastre em Administração › Comitês
              de Mudança.
            </Alert>
          )}
          <MultiSelect
            label={comite ? `Aprovadores — ${comite.name}` : 'Membros do CCM'}
            description={
              comite
                ? `Selecione ao menos ${comite.cab_quorum_required} do comitê (quórum). O solicitante não pode aprovar a própria mudança.`
                : `Selecione ao menos ${data.cab_quorum_required} aprovadores (quórum). O solicitante não pode aprovar a própria mudança.`
            }
            placeholder={comite ? 'Escolher no comitê' : 'Buscar usuários'}
            searchable
            data={
              comite
                // Com comitê a lista É o cadastro: oferecer quem o backend
                // rejeitaria seria erro só depois de preencher o formulário.
                ? comite.members
                    .filter((m) => m.user_id !== data.requested_by_user_id)
                    .map((m) => ({
                      value: String(m.user_id),
                      label: `${m.full_name ?? m.email ?? m.user_id} — ${APPROVER_ROLE_LABELS[m.approver_role] ?? m.approver_role}`,
                    }))
                // `/v1/users` mistura cadastros PENDENTES (`id: null`)
                // — ver utils/realUsers. Dois deles derrubam o seletor.
                : selectableUsers(usersData?.items ?? [])
                    .filter((u) => u.id !== data.requested_by_user_id)
                    .map((u) => ({ value: String(u.id), label: `${u.full_name} (${u.email})` }))
            }
            value={cabApprovers}
            onChange={setCabApprovers}
          />
          <TextInput
            label="Reunião do CCM (opcional, ISO 8601)"
            placeholder="2026-07-21T14:00:00Z"
            value={cabMeetingDate}
            onChange={(e) => setCabMeetingDate(e.target.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setCabModalOpen(false)}>Cancelar</Button>
            <Button
              size="xs"
              disabled={cabApprovers.length < data.cab_quorum_required}
              loading={sendToCabMutation.isPending}
              onClick={() => sendToCabMutation.mutate()}
            >
              Encaminhar
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={schedModalOpen} onClose={() => setSchedModalOpen(false)} title="Agendar Execução" size="md">
        <Stack gap="sm">
          <JanelaExecucaoFields
            tenantTimezone={data.tenant_timezone ?? schedTzEscolhido}
            userTimezone={user?.timezone ?? null}
            value={schedJanela}
            onChange={setSchedJanela}
            onTimezoneChange={setSchedTzEscolhido}
          />
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setSchedModalOpen(false)}>Cancelar</Button>
            <Button
              size="xs"
              disabled={!janelaValida(schedJanela)}
              loading={scheduleMutation.isPending}
              onClick={() => {
                const tz = data.tenant_timezone ?? schedTzEscolhido
                if (!tz || !schedJanela.start || !schedJanela.end) return
                scheduleMutation.mutate({
                  scheduled_start: wallClockToInstant(schedJanela.start, tz).toISOString(),
                  scheduled_end: wallClockToInstant(schedJanela.end, tz).toISOString(),
                })
              }}
            >
              Agendar
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={submitModalOpen} onClose={() => setSubmitModalOpen(false)} title="Janela de execução da GMUD" size="md">
        <Stack>
          <Text size="sm" c="dimmed">Esta GMUD não tem janela definida (ex.: promovida da esteira). Informe a janela para submeter.</Text>
          <JanelaExecucaoFields
            tenantTimezone={data.tenant_timezone ?? submitTzEscolhido}
            userTimezone={user?.timezone ?? null}
            value={submitJanela}
            onChange={setSubmitJanela}
            onTimezoneChange={setSubmitTzEscolhido}
          />
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setSubmitModalOpen(false)}>Cancelar</Button>
            <Button
              size="xs"
              loading={submitMutation.isPending}
              disabled={!janelaValida(submitJanela)}
              onClick={() => {
                // A janela e digitada no fuso do TENANT; convertemos para
                // instante absoluto aqui, para o TIMESTAMPTZ nao depender do
                // fuso da sessao do banco.
                const tz = data.tenant_timezone ?? submitTzEscolhido
                if (!tz || !submitJanela.start || !submitJanela.end) return
                submitMutation.mutate({
                  scheduled_start: wallClockToInstant(submitJanela.start, tz).toISOString(),
                  scheduled_end: wallClockToInstant(submitJanela.end, tz).toISOString(),
                })
              }}
            >
              Submeter
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Tabs defaultValue="visao-geral">
        <Tabs.List>
          <Tabs.Tab value="visao-geral">Visão Geral</Tabs.Tab>
          <Tabs.Tab value="rollback">Plano de Rollback</Tabs.Tab>
          <Tabs.Tab value="cab" rightSection={
            data.approvals.length > 0 ? <Badge size="xs" color="blue">{data.approvals.length}</Badge> : undefined
          }>CCM</Tabs.Tab>
          <Tabs.Tab value="execucao">Execução</Tabs.Tab>
          <Tabs.Tab value="historico">Histórico</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="visao-geral" pt="md">
          <SimpleGrid cols={2} spacing="md">
            <Paper shadow="xs" p="md" withBorder>
              <Stack gap="sm">
                <InfoRow label="Descrição" value={<Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{data.description || '—'}</Text>} />
                <Divider />
                <SimpleGrid cols={2}>
                  <InfoRow label="Solicitante" value={data.requested_by_name} />
                  <InfoRow label="Criado em" value={fmt(data.created_at)} />
                  <InfoRow label="Janela Início" value={fmt(data.scheduled_start)} />
                  <InfoRow label="Janela Fim" value={fmt(data.scheduled_end)} />
                </SimpleGrid>
                {data.affected_systems && data.affected_systems.length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Text size="xs" c="dimmed">Sistemas Afetados</Text>
                      <Group gap="xs" mt={4}>
                        {data.affected_systems.map((s, i) => (
                          <Badge key={i} size="xs" variant="outline">{s}</Badge>
                        ))}
                      </Group>
                    </Box>
                  </>
                )}
                {data.communication_plan && (
                  <>
                    <Divider />
                    <InfoRow label="Plano de Comunicação" value={data.communication_plan} />
                  </>
                )}
              </Stack>
            </Paper>
            <Paper shadow="xs" p="md" withBorder>
              <Stack gap="sm">
                <InfoRow label="Classificação" value={<GmudClassificationBadge classification={data.gmud_classification} />} />
                <InfoRow label="Nível de Risco" value={<GmudRiskBadge risk={data.risk_level} />} />
                <InfoRow label="Status" value={<StatusBadge status={data.status} withIcon />} />
                {data.cab_meeting_date && <InfoRow label="Reunião CAB" value={fmt(data.cab_meeting_date)} />}
                {data.execution_status && (
                  <InfoRow label="Resultado" value={<Badge color={data.execution_status === 'success' ? 'green' : 'red'} variant="light">{data.execution_status}</Badge>} />
                )}
              </Stack>
            </Paper>
          </SimpleGrid>
        </Tabs.Panel>

        <Tabs.Panel value="rollback" pt="md">
          <Paper shadow="xs" p="md" withBorder>
            <Stack gap="sm">
              <InfoRow label="Plano de Rollback" value={<Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{data.rollback_plan || '—'}</Text>} />
              <Divider />
              <SimpleGrid cols={2}>
                <InfoRow label="Tempo estimado" value={data.rollback_time_minutes ? `${data.rollback_time_minutes} minutos` : '—'} />
                <InfoRow label="Critério de acionamento" value={data.rollback_trigger_criteria} />
              </SimpleGrid>
            </Stack>
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="cab" pt="md">
          <Paper shadow="xs" p="md" withBorder>
            {data.approvals.length === 0 ? (
              <Stack align="center" gap="xs" py="md">
                <IconUsers size={32} color="var(--mantine-color-dimmed)" stroke={1.5} />
                <Text size="sm" c="dimmed">CCM ainda não convocado.</Text>
              </Stack>
            ) : (
              <GmudCabPanel
                gmudId={Number(id)}
                tenantId={tenantId}
                status={data.status}
                approvals={data.approvals}
                quorumRequired={data.cab_quorum_required}
                currentUserId={currentUserId}
                canVote={souAprovadorPendente}
              />
            )}
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="execucao" pt="md">
          <Paper shadow="xs" p="md" withBorder>
            <Stack gap="md">
              {data.execution_notes ? (
                <InfoRow label="Notas de Execução" value={<Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{data.execution_notes}</Text>} />
              ) : (
                <Stack align="center" gap="xs" py="md">
                  <IconRocket size={32} color="var(--mantine-color-dimmed)" stroke={1.5} />
                  <Text size="sm" c="dimmed">Execução ainda não iniciada.</Text>
                </Stack>
              )}
              {podeGerenciar && data.status === 'gmud_executing' && (
                <>
                  <Textarea label="Notas da execução" rows={3} value={execNotes} onChange={(e) => setExecNotes(e.target.value)} />
                  <Group>
                    <Button size="xs" color="green" onClick={() => finishExecMutation.mutate('success')} loading={finishExecMutation.isPending}>Concluída com Sucesso</Button>
                    <Button size="xs" color="red" variant="light" onClick={() => finishExecMutation.mutate('failed')} loading={finishExecMutation.isPending}>Falhou</Button>
                    <Button size="xs" color="orange" variant="light" onClick={() => finishExecMutation.mutate('rolled_back')} loading={finishExecMutation.isPending}>Rollback Executado</Button>
                  </Group>
                </>
              )}
              {/* Emergencial que falhou (`gmud_rolled_back`) também sai pela ratificação:
                  o backend não a fecha de outro jeito, e o botão "Fechar" daria erro. */}
              {podeGerenciar && emergenciaARatificar && (
                <>
                  <Textarea label="Post-Implementation Review (PIR)" rows={4} value={pirNotes} onChange={(e) => setPirNotes(e.target.value)} />
                  <Button size="xs" onClick={() => ratifyMutation.mutate()} loading={ratifyMutation.isPending}>Ratificar e Fechar GMUD</Button>
                </>
              )}
              {podeGerenciar && !emergenciaARatificar && ['gmud_completed', 'gmud_failed', 'gmud_rolled_back'].includes(data.status) && (
                <Button size="xs" variant="light" onClick={() => closeMutation.mutate()} loading={closeMutation.isPending}>
                  Fechar GMUD
                </Button>
              )}
              {data.pir_notes && <InfoRow label="PIR" value={<Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{data.pir_notes}</Text>} />}
            </Stack>
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="historico" pt="md">
          <Paper shadow="xs" p="md" withBorder>
            {data.events.length === 0 ? (
              <Stack align="center" gap="xs" py="md">
                <IconHistory size={32} color="var(--mantine-color-dimmed)" stroke={1.5} />
                <Text size="sm" c="dimmed">Nenhum evento registrado nesta GMUD ainda.</Text>
              </Stack>
            ) : (
              <Stack gap="xs">
                {data.events.map((ev) => (
                  <Group key={ev.id} gap="sm">
                    <Text size="xs" c="dimmed" w={130}>{fmt(ev.created_at)}</Text>
                    <Text size="xs" fw={500}>{ev.actor_name}</Text>
                    {ev.to_status && <StatusBadge status={ev.to_status} size="xs" />}
                    {ev.note && <Text size="xs" c="dimmed">{ev.note}</Text>}
                  </Group>
                ))}
              </Stack>
            )}
          </Paper>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  )
}
